/**
 * /comparisons/notion-ai — "Draftlize vs Notion AI for PMs" comparison
 * page. Targets the "notion ai alternative" keyword cluster.
 *
 * Content is English-only and inline (no i18n) — comparison pages are
 * long-tail SEO surfaces where Chinese/Japanese/German PMs are a
 * smaller slice; translate later if data warrants.
 */
import { MarketingShell } from '../MarketingShell';

export function NotionAi() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/comparisons/notion-ai"
      documentTitle="Notion Alternative for AI Product Work: Draftlize vs Notion AI · Draftlize"
      documentDescription="Looking for a Notion alternative built for AI product work? Notion AI bolts a chat button onto pages; Draftlize is a card graph an AI agent can read and write, so PM decisions don't drift. Side-by-side comparison."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Comparison · vs Notion AI</span>
          <h1
            className="serif"
            style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}
          >
            Draftlize vs Notion AI:<br/>
            <em>which one fits your PM workflow?</em>
          </h1>
          <p className="lede">
            Short answer: Notion AI is a chat helper bolted onto your notes. Draftlize is a substrate where AI agents can read AND write your product thinking as structured cards. They solve adjacent problems; you don't replace one with the other so much as decide which one you actually need.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">Side by side, <em>7 dimensions.</em></h2>
          </div>
          <p>Built from 6 weeks of PM dogfood data + side-by-side workflow runs. Tested with the same roadmap + same scenarios in both tools.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Notion AI</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Substrate shape</td><td className="ours yes">Typed card graph</td><td className="partial">Pages of blocks</td></tr>
            <tr><td>AI reads decisions as ambient context across chats</td><td className="ours yes">native</td><td className="no">—</td></tr>
            <tr><td>AI can write back to the substrate as structured cards</td><td className="ours yes">native</td><td className="partial">writes blocks but no structure</td></tr>
            <tr><td>Stale propagation when a dependency changes</td><td className="ours yes">native</td><td className="no">—</td></tr>
            <tr><td>Cross-thread / cross-page conflict detection</td><td className="ours yes">native</td><td className="no">—</td></tr>
            <tr><td>Free-form rich docs, embeds, databases</td><td className="no">—</td><td className="yes">excellent</td></tr>
            <tr><td>Team wiki / collaboration / permissions</td><td className="no">solo only (v1)</td><td className="yes">excellent</td></tr>
            <tr><td>Claude Code / MCP / first-class AI tool integration</td><td className="ours yes">native</td><td className="no">—</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Where <em>Notion AI wins.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">If you need a wiki</h3>
            <p>Notion is the best wiki tool that exists. If your problem is "where does our team store the runbook for incident response," Notion. Draftlize isn't trying to be a wiki.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">If you live in tables + databases</h3>
            <p>Notion's database / view system is uncatchable. Roadmap tracker, sprint planning grids, vendor evaluation tables — Notion's structured collections beat raw cards every time for those shapes.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">If your team is &gt;5 people</h3>
            <p>Real-time collaboration, fine-grained permissions, comments, mentions — Notion was built for it. Draftlize is solo-first by design.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Where <em>Draftlize wins.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">AI agents that <em>actually</em> remember</h3>
            <p>Notion AI's "memory" is best-effort retrieval into chat context. The agent can read a page if you @-mention it. In Draftlize, the agent reads <code>c-04</code> (your pricing decision) automatically every chat, because cards are the substrate the agent IS working in. Re-explanation goes to zero.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Spec drift becomes visible</h3>
            <p>Change a Notion page → nothing notices. Change a Draftlize decision card → every dependent card auto-flags <code>stale: true</code>. The substrate tracks invalidation the way a build system tracks invalidated artifacts.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">First-class context for Claude Code / Cursor / MCP</h3>
            <p>Notion AI lives in Notion. Draftlize cards land in your repo via CLI, surface via MCP server in Claude Code / Cursor / Claude Desktop, and ship with 4 slash commands. Decisions are addressable from any AI tool — not just one.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Notion is the answer to "where does our team write things down." Draftlize is the answer to "where does our product thinking compound when AI agents are doing half the work."
        </blockquote>
        <cite>If you only need the first answer, Notion is enough.</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start free with <em>$5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            New accounts start with $5 free, pay only for what you use, balance never expires. You can keep Notion. Draftlize is the substrate underneath the AI-assisted parts.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
